<script setup>
import { defineEmits, defineProps } from 'vue';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

const props = defineProps({
  title: {
    type: String,
    default: 'Are you sure?'
  },
  message: {
    type: String,
    default: ''
  },
  confirmLabel: {
    type: String,
    default: 'Confirm'
  },
  cancelLabel: {
    type: String,
    default: 'Cancel'
  },
  /**
   * Visual variant for the confirm button.
   * 'danger'  → red  (destructive actions like delete)
   * 'warning' → amber
   * 'default' → uses .default-button
   */
  variant: {
    type: String,
    default: 'danger',
    validator: (v) => ['danger', 'warning', 'default'].includes(v)
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['confirm', 'cancel']);

function cancel() {
  if (props.loading) return;
  emit('cancel');
}

const backdrop = useModalBackdropClose(cancel);
</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 flex justify-center items-center z-[60] p-4"
    @mousedown.self="backdrop.onBackdropMousedown"
    @mouseup.self="backdrop.onBackdropMouseup"
  >
    <div
      class="ui-modal rounded-lg shadow-xl w-full max-w-md flex flex-col border border-gray-200 dark:border-gray-600"
      role="dialog"
      aria-modal="true"
      @mousedown="backdrop.onContentMousedown"
    >
      <div class="p-6">
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
        <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">{{ title }}</h2>

        <!-- Slot lets callers pass rich content; falls back to the message prop -->
        <slot>
          <p class="mt-3 text-gray-600 dark:text-gray-400">{{ message }}</p>
        </slot>

        <p v-if="error" class="mt-3 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
      </div>

      <div class="p-6 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-600">
        <button
          type="button"
          :disabled="loading"
          class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160 disabled:opacity-50 disabled:pointer-events-none"
          @click="cancel"
        >
          {{ cancelLabel }}
        </button>
        <button
          type="button"
          :disabled="loading"
          class="default-button inline-flex items-center gap-2"
          @click="emit('confirm')"
        >
          <span
            v-if="loading"
            class="inline-block size-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
          {{ loading ? confirmLabel + '…' : confirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>
